import React from "react";
import { arrayUtil, InspectionPointStatus, SafetyInspection } from "../../../../../amorg-common";
import { Card } from "../../../../../designSystem/Card";
import { Button } from "../../../../../designSystem/button/Button";
import { colors } from "../../../../../designSystem/colors/color";
import { Flex } from "../../../../../designSystem/compositeComponents/FlexHelpers";
import { FormField } from "../../../../../designSystem/formField/FormField";
import { SegmentBar } from "../../../../../designSystem/graphs/SegmentBar";
import { Icon } from '../../../../../designSystem/icons/Icons';
import { StateDetails } from "../../inspections/[facilityInspectionId]/InspectionPointPage";
import { getFacilityTypeIcon } from '../../../../../utils/facilityHelper';
import { InstructionText } from "./InstructionText";
import { Span, Span14 } from "../../../../../designSystem/typography/Body";
import { InfoChip } from "../../../../../designSystem/Chip";
import { H2 } from "../../../../../designSystem/typography/Heading";
import { InspectionStatePanel } from "./InspectionStatePanel";

type InspectionOverviewProps = {
    activityId:string,
    safetyInspection:SafetyInspection,
    details:StateDetails,
    status:InspectionPointStatus,
    comment:string,
    showSelection?:boolean,
    onInspectionStateChange:(status:InspectionPointStatus, comment:string) => void,
    onRedirect:(url:string)=>void,
}
export function InspectionOverview({activityId, safetyInspection, details, status, comment, showSelection = true, onInspectionStateChange, onRedirect}:InspectionOverviewProps):JSX.Element
{
    const flatData = safetyInspection.locations.flatMap(l => l.facilities.flatMap(f => f.inspectionPoints.flatMap(ip => ({ locationId:l.locationId, facilityId:f.facilityId, inspection:ip }))));
    const flatDataIndex = flatData.findIndex(x => x.inspection.inspectionResult.id === details.inspection.inspectionResult.id);
    const previousFlatData = flatData.slice(0, flatDataIndex);
    const nextFlatData = flatData.slice(flatDataIndex+1);

    function getFacilityRedirect(dir:'back'|'forward')
    {
        // back <- first inspection of last facility
        // forward -> first inspection of next facility
        if(dir === 'back')
        {
            const previousFacility = arrayUtil.lastWhere(previousFlatData, (fd) => fd.facilityId !== details.facility.facilityId);
            if(previousFacility !== undefined){
                const firstInspectionPoint = arrayUtil.firstWhere(previousFlatData, (fd) => fd.facilityId === previousFacility.facilityId)?.inspection;
                if(firstInspectionPoint === undefined)
                    return;
                return () => onRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        else if(dir === 'forward')
        {
            const nextFacility = arrayUtil.firstWhere(nextFlatData, (fd) => fd.facilityId !== details.facility.facilityId);
            if(nextFacility !== undefined){
                const firstInspectionPoint = arrayUtil.firstWhere(nextFlatData, (fd) => fd.facilityId === nextFacility.facilityId)?.inspection;
                if(firstInspectionPoint === undefined)
                    return;
                return () => onRedirect(`/safetyinspections/${activityId}/inspections/${firstInspectionPoint.inspectionResult.id}`);
            }
        }
        return undefined;
    }

    return (
        <Flex flexDirection='column' gap={32}>
            <Card variant="outlined">
                <SegmentBar
                    gapPx={1}
                    totalSegments={details.allInspections.length}
                    selectedIndex={showSelection ? details.currentTotalIndex : undefined}
                    height={30}
                    selectionPxIncrease={10}
                    segments={details.allInspections.map((x) =>
                        ({
                            color:  x.inspectionResult.status === 'passed' ? colors.graphs.segmentBar.success
                                    : x.inspectionResult.status === 'failed' ? colors.graphs.segmentBar.failure
                                    : colors.graphs.segmentBar.default,
                            onClick: () => onRedirect(`/safetyinspections/${activityId}/inspections/${x.inspectionResult.id}`)
                        })
                    )}
                />
                <Flex justifyContent='center'>
                    <Span14 style={{textAlign:'center'}}>
                        {details.allInspections.filter(x => x.inspectionResult.status !== 'pending').length} ud af {details.allInspections.length} kontrolpunkter gennemført
                    </Span14>
                </Flex>
            </Card>

            <Card variant="outlined">
                <Flex flexDirection="column" gap={16}>
                    <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <InfoChip>{details.location.name}</InfoChip>
                        <Span14 color="gray" fontWeight={400}>Facilitet {details.facilityIndex + 1} af {details.location.facilities.length}</Span14>
                    </Flex>
                    <Flex>
                        <Flex flexGrow="1">
                            <Flex gap={16} alignItems="center">
                                <Icon name={getFacilityTypeIcon(details.facility.facilityType.id)} />
                                <Flex flexDirection="column">
                                    <Span size="18px" fontWeight={450}>{details.facility.name}</Span>
                                </Flex>
                            </Flex>
                        </Flex>
                        <Flex gap={4}>
                            <Button icon="caret_caretBack" variant="outlined" size="32px" disabled={getFacilityRedirect('back') === undefined}
                                onClick={() => getFacilityRedirect('back')?.()} />
                            <Button icon="caret_caretForward" variant="outlined" size="32px" disabled={getFacilityRedirect('forward') === undefined}
                                onClick={() => getFacilityRedirect('forward')?.()} />
                        </Flex>
                    </Flex>

                </Flex>
            </Card>
            
            <Card variant="outlined">
                <Flex flexDirection='column' gap={16}>
                    <Flex>
                        <Card variant="lightGray" padding="4px 8px">
                            <Flex gap={4} alignItems="center">
                                <Icon name="fact_check" />
                                <Span14 fontWeight={400}>Kontrolpunkt {details.inspectionIndex + 1} af {details.facility.inspectionPoints.length}</Span14>
                            </Flex>
                        </Card>
                    </Flex>

                    <H2>{details.inspection.inspectionPoint.title}</H2>

                    <FormField
                        label="Vejledning"
                        inputComponent={
                            <Card variant="outlined">
                                <InstructionText text={details.inspection.inspectionPoint.instructions} defaultCollapsed={true} />
                            </Card>
                        } />

                    <InspectionStatePanel
                        status={status}
                        comment={comment}
                        onChange={onInspectionStateChange}
                    />

                </Flex>
            </Card>
        </Flex>
    );
}